/**
 * How much of the free trial is left, out of the trial the server reported.
 *
 * The dates come from `GET /billing/subscription/` — see `trialService` — so
 * nothing here decides *whether* a trial is running: `active` is the server's
 * `is_active`, and a trial it calls finished counts for nothing however the
 * dates read on this machine. What is worked out here is only the arithmetic a
 * countdown needs, which cannot come off the wire: the wire carries an instant,
 * and the rail draws a number of days against the reader's own clock.
 *
 * Kept apart from `trialState`, which is this app's guess at a trial that has
 * not been started yet. This one is about a trial that has.
 */

import { TRIAL_DAYS } from "./trialState";
import type { Trial } from "./types/subscriptions.types";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * How near the end counts as near it — the last three days.
 *
 * What the foot of the rail draws turns on this. For most of the fortnight the
 * trial is a fact somebody glances at, and a quiet bar is the whole of what it
 * owes them; in the last few days it is a deadline, and the rail says so with
 * the card and the way onto a plan. One number, here rather than in either of
 * the two components, so they cannot disagree about when the trial got urgent
 * and draw both or neither.
 */
export const TRIAL_URGENT_DAYS = 3;

export interface TrialCountdown {
  /** Whole days still to run, never below zero. */
  daysLeft: number;
  /** How long the fortnight was, so the bar has something to be a fraction of. */
  totalDays: number;
  /** Whole days already spent — `totalDays - daysLeft`. */
  daysUsed: number;
  /** How much of the trial has gone, `0…1`. This is what the bar fills to: it
   *  starts empty on the day the trial is taken and is full on the last one. */
  elapsed: number;
  /** What the number says out loud: "6 days left", "1 day left", "Ends today". */
  label: string;
  /** Whether the end is near enough to press — `TRIAL_URGENT_DAYS` or fewer
   *  still to run. The rail draws a different thing on each side of it. */
  urgent: boolean;
}

/** Days between two instants, rounded up: a trial with six hours left is still
 *  a day the dietitian has, and rounding it to zero would say it is over. */
const daysBetween = (from: Date, to: Date): number =>
  Math.ceil((to.getTime() - from.getTime()) / MS_PER_DAY);

/**
 * The countdown, or `null` where there is nothing to count.
 *
 * `null` for a workspace that never took a trial, for one whose trial the
 * server calls finished, and for a trial that somehow arrived without an end
 * date — a meter that cannot say how much is left is worse than no meter.
 *
 * `totalDays` is measured off the trial's own two dates rather than assumed to
 * be `TRIAL_DAYS`, so a trial the backend extended draws a bar that is honest
 * about its own length. `TRIAL_DAYS` is only the fallback for a trial that
 * reports an end but not a start.
 */
export const trialCountdown = (
  trial: Trial | null | undefined,
  now: Date = new Date()
): TrialCountdown | null => {
  if (!trial?.active || !trial.endsOn) return null;

  const totalDays = trial.startedOn
    ? Math.max(1, daysBetween(trial.startedOn, trial.endsOn))
    : TRIAL_DAYS;

  // Capped at the length of the trial as well as floored at zero: a laptop
  // whose clock is a week behind must not draw a fifteenth day on a fortnight.
  const daysLeft = Math.min(totalDays, Math.max(0, daysBetween(now, trial.endsOn)));

  const daysUsed = totalDays - daysLeft;

  return {
    daysLeft,
    totalDays,
    daysUsed,
    elapsed: daysUsed / totalDays,
    urgent: daysLeft <= TRIAL_URGENT_DAYS,
    label:
      daysLeft === 0
        ? "Ends today"
        : `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`,
  };
};
